/**
 * Make video embeds responsive in WordPress block markup.
 *
 * A published core/embed only sizes to the column when its figure carries the aspect-ratio classes
 * (`wp-embed-aspect-16-9 wp-has-aspect-ratio`) and the block comment carries the matching
 * `className` and `responsive: true`; WordPress core's CSS (under `body.wp-embed-responsive`) keys
 * off those classes. Gutenberg derives them from the oEmbed preview's iframe size, but a draft
 * that went through our JSON round trip, or a block whose preview had not resolved when it was
 * serialized, comes out without them, and WordPress then renders the provider's default iframe
 * (500x281 for YouTube), small and cropped inside a full-width column.
 *
 * Video providers (YouTube, Vimeo, ...) are 16:9 by default; embeds that already carry an aspect
 * class, and non-video embeds, are left exactly as they are.
 */

export const VIDEO_ASPECT_CLASSES = 'wp-embed-aspect-16-9 wp-has-aspect-ratio';

const EMBED_OPEN_RE = /<!--\s*wp:embed(\s+\{[\s\S]*?\})?\s*-->(\s*)<figure\s+class="([^"]*)"/g;

const mergeClassName = (existing: unknown, add: string): string => {
  const have = String(existing || '')
    .split(/\s+/)
    .filter(Boolean);
  add.split(' ').forEach((c) => {
    if (!have.includes(c)) have.push(c);
  });
  return have.join(' ');
};

export const ensureResponsiveEmbeds = (html: string): string => {
  if (!html || typeof html !== 'string' || !/wp:embed/.test(html)) return html;
  return html.replace(EMBED_OPEN_RE, (whole, json: string | undefined, ws: string, cls: string) => {
    if (!/\bis-type-video\b/.test(cls) || /\bwp-embed-aspect-/.test(cls)) return whole;
    let attrs: Record<string, unknown> = {};
    if (json) {
      try {
        attrs = JSON.parse(json.trim());
      } catch {
        return whole; // never rewrite a comment we cannot read
      }
    }
    attrs = { ...attrs, responsive: true, className: mergeClassName(attrs.className, VIDEO_ASPECT_CLASSES) };
    return `<!-- wp:embed ${JSON.stringify(attrs)} -->${ws}<figure class="${cls.trim()} ${VIDEO_ASPECT_CLASSES}"`;
  });
};
